import { useState } from 'react'
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion } from 'framer-motion'
import {
  LayoutDashboard, Ticket, GitBranch, FileText, Settings as SettingsIcon,
  ShieldCheck, LogOut, Menu, X, ChevronLeft, Bell, CreditCard,
} from 'lucide-react'
import clsx from 'clsx'
import { useAppStore } from '../../store/useAppStore'
import { AGENTS } from '../../lib/agents'
import LanguageSwitcher from '../common/LanguageSwitcher'

const NAV = [
  { to: '/app/dashboard', icon: LayoutDashboard, key: 'nav.dashboard' },
  { to: '/app/tickets', icon: Ticket, key: 'nav.tickets' },
  { to: '/app/workflow', icon: GitBranch, key: 'nav.workflow' },
  { to: '/app/documents', icon: FileText, key: 'nav.documents' },
  { to: '/app/billing', icon: CreditCard, key: 'nav.billing' },
  { to: '/app/settings', icon: SettingsIcon, key: 'nav.settings' },
]

function SidebarContent({ collapsed, onNavigate }: { collapsed: boolean; onNavigate?: () => void }) {
  const { t } = useTranslation()
  const user = useAppStore((s) => s.user)
  const location = useLocation()

  return (
    <div className="flex h-full flex-col">
      <Link to="/" className="flex h-16 shrink-0 items-center gap-3 px-5" onClick={onNavigate}>
        <img src="/logos/gback_logo_icon.svg" alt="G BACK" className="h-9 w-9" />
        {!collapsed && <span className="font-display text-lg font-bold tracking-tight text-white">G BACK</span>}
      </Link>

      <nav className="scrollbar-none flex-1 space-y-6 overflow-y-auto px-3 pb-6" aria-label={t('nav.main', 'Navigation')}>
        <div className="space-y-1">
          {NAV.map(({ to, icon: Icon, key }) => (
            <NavLink
              key={to}
              to={to}
              onClick={onNavigate}
              className={({ isActive }) =>
                clsx(
                  'group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors',
                  isActive ? 'bg-primary-500/15 text-primary-300' : 'text-night-300 hover:bg-white/5 hover:text-white'
                )
              }
            >
              <Icon className="h-[18px] w-[18px] shrink-0" aria-hidden />
              {!collapsed && <span className="truncate">{t(key)}</span>}
            </NavLink>
          ))}
        </div>

        <div>
          {!collapsed && (
            <p className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-night-500">
              {t('nav.agents')}
            </p>
          )}
          <div className="space-y-0.5">
            {AGENTS.map(({ type, icon: Icon, color, bg }) => {
              const active = location.pathname === `/app/agent/${type}`
              return (
                <NavLink
                  key={type}
                  to={`/app/agent/${type}`}
                  onClick={onNavigate}
                  className={clsx(
                    'group flex items-center gap-3 rounded-xl px-3 py-2 text-sm transition-colors',
                    active ? 'bg-white/8 text-white' : 'text-night-300 hover:bg-white/5 hover:text-white'
                  )}
                  title={t(`agents.${type}`)}
                >
                  <span
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition-transform group-hover:scale-105"
                    style={{ backgroundColor: bg }}
                  >
                    <Icon className="h-4 w-4" style={{ color }} aria-hidden />
                  </span>
                  {!collapsed && <span className="truncate">{t(`agents.${type}`)}</span>}
                </NavLink>
              )
            })}
          </div>
        </div>

        {user?.role === 'admin' && (
          <NavLink
            to="/app/admin"
            onClick={onNavigate}
            className={({ isActive }) =>
              clsx(
                'flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors',
                isActive ? 'bg-primary-500/15 text-primary-300' : 'text-night-300 hover:bg-white/5 hover:text-white'
              )
            }
          >
            <ShieldCheck className="h-[18px] w-[18px]" aria-hidden />
            {!collapsed && <span>{t('nav.admin', 'Administration')}</span>}
          </NavLink>
        )}
      </nav>
    </div>
  )
}

export default function MainLayout() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [mobileOpen, setMobileOpen] = useState(false)
  const sidebarOpen = useAppStore((s) => s.sidebarOpen)
  const toggleSidebar = useAppStore((s) => s.toggleSidebar)
  const user = useAppStore((s) => s.user)
  const logout = useAppStore((s) => s.logout)
  const notifications = useAppStore((s) => s.notifications)
  const unread = notifications.filter((n) => !n.read).length

  const handleLogout = () => {
    logout()
    navigate('/')
  }

  return (
    <div className="flex min-h-screen bg-night-950">
      {/* Sidebar desktop */}
      <aside
        className={clsx(
          'sticky top-0 hidden h-screen shrink-0 border-r border-white/5 bg-night-900/70 backdrop-blur-xl transition-[width] duration-300 lg:block',
          sidebarOpen ? 'w-64' : 'w-[76px]'
        )}
      >
        <SidebarContent collapsed={!sidebarOpen} />
      </aside>

      {/* Sidebar mobile */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileOpen(false)}
            />
            <motion.aside
              className="fixed inset-y-0 left-0 z-50 w-72 border-r border-white/5 bg-night-900 lg:hidden"
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            >
              <button
                className="absolute right-3 top-4 rounded-lg p-2 text-night-300 hover:bg-white/5"
                onClick={() => setMobileOpen(false)}
                aria-label={t('common.close', 'Fermer')}
              >
                <X className="h-5 w-5" />
              </button>
              <SidebarContent collapsed={false} onNavigate={() => setMobileOpen(false)} />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-white/5 bg-night-950/80 px-4 backdrop-blur-xl sm:px-6">
          <button
            className="rounded-lg p-2 text-night-300 hover:bg-white/5 hover:text-white lg:hidden"
            onClick={() => setMobileOpen(true)}
            aria-label={t('nav.openMenu', 'Ouvrir le menu')}
          >
            <Menu className="h-5 w-5" />
          </button>
          <button
            className="hidden rounded-lg p-2 text-night-300 hover:bg-white/5 hover:text-white lg:block"
            onClick={toggleSidebar}
            aria-label={t('nav.toggleSidebar', 'Réduire le menu')}
          >
            <ChevronLeft className={clsx('h-5 w-5 transition-transform', !sidebarOpen && 'rotate-180')} />
          </button>

          <div className="flex-1" />

          <LanguageSwitcher />

          <button
            className="relative rounded-lg p-2 text-night-300 hover:bg-white/5 hover:text-white"
            aria-label={t('nav.notifications', 'Notifications')}
          >
            <Bell className="h-5 w-5" />
            {unread > 0 && (
              <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary-500 px-1 text-[10px] font-bold text-white">
                {unread}
              </span>
            )}
          </button>

          <div className="ml-1 flex items-center gap-3 border-l border-white/10 pl-4">
            <div className="hidden text-right sm:block">
              <p className="text-sm font-medium text-white">{user ? `${user.firstName} ${user.lastName}`.trim() : ''}</p>
              <p className="text-xs capitalize text-night-400">{user?.role}</p>
            </div>
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-primary-500 to-sand-500 font-display text-sm font-bold text-white">
              {(user?.firstName?.[0] || 'U').toUpperCase()}
            </div>
            <button
              className="rounded-lg p-2 text-night-300 hover:bg-white/5 hover:text-red-400"
              onClick={handleLogout}
              aria-label={t('nav.logout', 'Déconnexion')}
            >
              <LogOut className="h-[18px] w-[18px]" />
            </button>
          </div>
        </header>

        <main className="flex-1 px-4 py-8 sm:px-6 lg:px-8">
          <div className="mx-auto w-full max-w-7xl animate-fade-up">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  )
}
